<template>
<div>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>

  <div class="row">
    <div class="col">
      <card>
        <div class="row mb-2">
          <div class="col">
            <h4 class="card-title">Reports</h4>
          </div>
          <div class="col text-right">
            <base-button size="sm" v-on:click="createNewReport()" type="primary" >
              <i class="tim-icons icon-simple-add mr-2"></i> Create custom report</base-button>
          </div>
        </div>
        <filter-bar v-if="firstLoadDone && (totalRows > 0 || hasActiveFilters)"
                    :filters="filterSchema"
                    :initial-value="filterValues"
                    :result-count="totalRows"
                    :loading="loading"
                    :per-page="perPage"
                    :per-page-options="perPageOptions"
                    saved-filters-key="reports-list"
                    @change="onFiltersChanged"
                    @update:perPage="onPerPageChange"
        />

        <!--   No data panel -->
        <div v-if="firstLoadDone && totalRows <= 0 && !hasActiveFilters" class="mt-3" >
          <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
            <h4 class="mt-3">No reports have been created.</h4>
          </div>
        </div>

        <div v-if="firstLoadDone && totalRows <= 0 && hasActiveFilters" class="mt-3">
          <p>No reports match the current filters.</p>
        </div>

        <b-table v-if="reports.length > 0" striped hover :items="reports"
                 :fields="['name', 'type', { key: 'date_range', label: 'Scope' }, 'created_by', 'last_generated_by', 'actions']">
          <template v-slot:cell(name)="row">
            <p>{{row.item.name}}</p>
            <small class="d-block">{{row.item.description}}</small>
            <small v-if="groups.length > 1" class="text-muted">{{row.item.group_name}}</small>
          </template>
          <template v-slot:cell(type)="row">
            <badge type="primary">{{ gameToEventLabel(row.item.report_type) }}</badge>
            <badge v-if="row.item.is_summary === 1" type="info" class="ml-1">Summary</badge>
          </template>
          <template v-slot:cell(date_range)="row">
            <span v-if="!row.item.is_date_range">All time</span>
            <span v-else-if="row.item.time_period_type_id !== 16">{{ row.item.time_range_name }}</span>
            <span v-else>
              {{getFormattedDate(row.item.report_start_date)}} - {{getFormattedDate(row.item.report_end_date)}}
            </span>
            <small v-if="presetFilterLabel(row.item)" class="d-block">{{ presetFilterLabel(row.item) }}</small>
          </template>
          <template v-slot:cell(created_by)="row">
            <p>{{row.item.first_name}} {{row.item.last_name}}</p>
            <small>{{getLocalDate(row.item.created_date)}}, {{getLocalTime(row.item.created_date)}}</small>
          </template>
          <template v-slot:cell(last_generated_by)="row">
            <p v-if="row.item.last_run_date">{{row.item.ran_by}}</p>
            <small v-if="row.item.last_run_date">{{getLocalDate(row.item.last_run_date)}}, {{getLocalTime(row.item.last_run_date)}}</small>
            <span v-else>-</span>
          </template>
          <template v-slot:cell(actions)="row">
            <el-dropdown>
              <base-button
                simple
                type="primary"
                link
              > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
              </base-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <a class="dropdown-item mt-2 mb-2" @click.prevent="runReport(row.item)" href="#">
                    <i class="tim-icons icon-triangle-right-17 text-default mr-2"></i> Run report
                  </a>
                  <a class="dropdown-item mt-2 mb-2" v-if="row.item.can_write !== 0" @click.prevent="editReport(row.item)" href="#">
                    <i class="tim-icons icon-pencil text-warning mr-2"></i> Edit report
                  </a>
                  <hr v-if="row.item.can_write !== 0">
                  <a v-if="row.item.can_write !== 0" class="dropdown-item mt-2 mb-2" @click.prevent="deleteReportPrompt(row.item)" href="#">
                    <i class="tim-icons icon-trash-simple text-danger mr-2"></i> Delete report
                  </a>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </b-table>

        <b-pagination v-if="totalRows > perPage"
                      @input="getReports()"
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
        ></b-pagination>
      </card>
    </div>
  </div>

</div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import swal from "sweetalert2";
import {gameToEventLabel} from "@/util/eventLabels";
import { BTable, BPagination } from 'bootstrap-vue'
import Badge from "@/components/Badge";
import {dayOfWeek, formattedDate, time} from "@/plugins/dateFormatter";
import {Dropdown, DropdownItem, DropdownMenu} from "element-ui";

export default {
  name: "ReportBuilder",
  components: {BTable, BPagination, Badge, [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,},
  data() {
    return {
      loading: false,
      reports: [],
      report_types: [],
      groups: [],
      filterValues: { name: '', report_type_id: [], company_group_id: [], mode: '' },
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 25, 50],
      totalRows: 0,
      firstLoadDone: false,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
    }
  },
  computed: {
    filterSchema() {
      return [
        { key: 'name', type: 'text', label: 'Name', operatorLabel: 'contains', primary: true },
        {
          key: 'report_type_id', type: 'multiselect', label: 'Type', primary: true,
          options: (this.report_types || []).map(rt => ({ label: this.gameToEventLabel(rt.name), value: rt.id }))
        },
        {
          key: 'company_group_id', type: 'multiselect', label: 'Group', primary: true,
          options: (this.groups || []).map(g => ({ label: g.name, value: g.id }))
        },
        {
          key: 'mode', type: 'select', label: 'Mode', primary: true,
          options: [{ label: 'Plain', value: 'plain' }, { label: 'Summary', value: 'summary' }]
        }
      ]
    },
    hasActiveFilters() {
      return (this.filterValues.name || '') !== ''
        || (this.filterValues.report_type_id || []).length > 0
        || (this.filterValues.company_group_id || []).length > 0
        || (this.filterValues.mode || '') !== ''
    }
  },
  methods: {
    onFiltersChanged(values) {
      this.filterValues = { ...this.filterValues, ...values }
      this.currentPage = 1
      this.getReports()
    },
    onPerPageChange(v) {
      this.perPage = v
      this.currentPage = 1
      this.getReports()
    },
    gameToEventLabel,
    getDayOfWeek(d){
      if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
        return dayOfWeek(d) + ', '
      }
      return ''
    },
    getTime(d) {
      return time(this.time_format, d)
    },
    getFormattedDate(d){
      if(d instanceof Date){
        d = d.getFullYear() + '-' + (d.getMonth()+1) + '-' + d.getDate()
      }
      return formattedDate(this.date_format, d)
    },
    getLocalTime(d){
      let local = new Date(d)
      return this.getTime(local.getHours() + ':' + local.getMinutes())
    },
    getLocalDate(d){
      let local = new Date(d)
      let form = local.getFullYear() + "-" + (local.getMonth() + 1) + "-" + local.getDate();
      let final = this.getFormattedDate(form)
      return this.getDayOfWeek(form) + final
    },
    deleteReportPrompt(item){
      swal({
        title: 'Are you sure?',
        text: `This report will be permanently deleted`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete'
      }).then((response)=> {
        if(response.value == true){
          this.deleteReport(item);
        }
      })
    },
    async deleteReport(item){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/reports/' + item.id

      this.axios.delete(url, config)
        .then(response => {
          this.loading = false
          swal('Success!', 'Report has been deleted', 'success')
          this.getReports()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    presetFilterLabel(item){
      if(!item.filters) return ''
      try {
        const keys = Object.keys(JSON.parse(item.filters))
        if(keys.length === 0) return ''
        return 'Filtered: ' + keys.map(k => this.gameToEventLabel(k.replaceAll('_', ' '))).join(', ')
      } catch (err) {
        return ''
      }
    },
    editReport(item){
      this.$router.push('/reports/edit?id=' + item.id)
    },
    createNewReport(){
      this.$router.push('/reports/new')
    },
    runReport(item){
      this.$router.push('/reports/run?id=' + item.id)
    },
    async getReports(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/reports'
        + '?offset=' + ((this.currentPage - 1) * this.perPage) + '&count=' + this.perPage
      if((this.filterValues.name || '') !== ''){
        url += '&name=' + encodeURIComponent(this.filterValues.name)
      }
      if((this.filterValues.report_type_id || []).length > 0){
        url += '&report_type_ids=' + encodeURIComponent(JSON.stringify(this.filterValues.report_type_id))
      }
      if((this.filterValues.company_group_id || []).length > 0){
        url += '&company_group_ids=' + encodeURIComponent(JSON.stringify(this.filterValues.company_group_id))
      }
      if((this.filterValues.mode || '') !== ''){
        url += '&mode=' + encodeURIComponent(this.filterValues.mode)
      }

      this.axios.get(url, config)
        .then(response => {
          this.loading = false
          this.reports = response.data.reports
          this.report_types = response.data.report_types || []
          this.groups = response.data.groups || []
          this.totalRows = response.data.total_rows || 0
          this.firstLoadDone = true
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    }
  },
  mounted() {
    this.getReports()
  }
}
</script>

<style scoped>

</style>
